
import React, { useState } from "react";
import LazyImage from "@/components/LazyImage";
import { useBlogImages } from "@/hooks/useBlogImages";
import { Helmet } from "react-helmet-async";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BlogImageGalleryProps {
  showTitle?: boolean;
}

const BlogImageGallery: React.FC<BlogImageGalleryProps> = ({ 
  showTitle = true 
}) => {
  const { images, loading, error } = useBlogImages();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  if (loading) {
    return (
      <div className="py-8" role="status" aria-label="Chargement des images du blog">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(12)].map((_, index) => (
            <div 
              key={index} 
              className="aspect-w-16 aspect-h-9 bg-gray-200 animate-pulse rounded-lg" 
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center" role="alert">
        <p className="text-gray-600">Impossible de charger les images du blog</p>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-600">Aucune image disponible pour le moment</p>
      </div>
    );
  }

  // Générer un nom descriptif basé sur le nom du fichier
  const generateImageTitle = (filename: string, index: number) => {
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
    const cleanName = nameWithoutExt
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase());
    return cleanName || `Installation recyclage valorisation déchets ${index + 1}`;
  };

  // Générer un alt text SEO optimisé
  const generateImageAlt = (filename: string, index: number) => {
    const title = generateImageTitle(filename, index);
    return `${title} - Site valorisation déchets métaux Groupe Fabre Environnement recyclage France installation industrielle`;
  };

  // Générer les données structurées pour chaque image
  const generateImageSchema = (image: any, index: number) => {
    const title = generateImageTitle(image.name, index);
    const alt = generateImageAlt(image.name, index);
    
    return {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "url": image.url,
      "name": title,
      "alternateName": alt,
      "description": `Photographie des installations de recyclage et valorisation des déchets métalliques - ${title}`,
      "contentUrl": image.url,
      "thumbnailUrl": image.url,
      "width": "400",
      "height": "256",
      "encodingFormat": "image/jpeg",
      "uploadDate": new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": "Groupe Fabre Environnement",
        "url": "https://www.groupefabre-environnement.fr"
      },
      "copyrightHolder": {
        "@type": "Organization",
        "name": "Groupe Fabre Environnement"
      },
      "license": "https://www.groupefabre-environnement.fr/mentions-legales",
      "acquireLicensePage": "https://www.groupefabre-environnement.fr/contact",
      "creditText": "© Groupe Fabre Environnement",
      "creator": {
        "@type": "Organization",
        "name": "Groupe Fabre Environnement"
      },
      "isPartOf": {
        "@type": "WebPage",
        "name": "ValoGuide - Guide Valorisation Déchets",
        "url": "https://www.groupefabre-environnement.fr/valoguide"
      },
      "keywords": "recyclage, valorisation déchets, métaux, environnement, installation industrielle, Groupe Fabre, France, équipements, traitement déchets"
    };
  };

  // Fonctions pour la lightbox
  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImageIndex(null);
  };

  const goToPrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const currentImage = selectedImageIndex !== null ? images[selectedImageIndex] : null;

  // Générer le schéma global de la galerie
  const generateGallerySchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "name": "Galerie Photos Installations Valorisation Déchets - Groupe Fabre Environnement",
      "description": "Collection photographique des sites de recyclage et valorisation des déchets métalliques du Groupe Fabre Environnement à travers la France",
      "url": "https://www.groupefabre-environnement.fr/valoguide",
      "mainEntity": {
        "@type": "Organization",
        "name": "Groupe Fabre Environnement"
      },
      "numberOfItems": images.length,
      "image": images.map((image, index) => generateImageSchema(image, index)),
      "about": {
        "@type": "Thing",
        "name": "Valorisation et recyclage des déchets métalliques"
      },
      "keywords": "galerie photos, recyclage, valorisation déchets, installations industrielles, métaux, environnement, France"
    };
  };

  return (
    <section className="py-12" itemScope itemType="https://schema.org/ImageGallery">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(generateGallerySchema())}
        </script>
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:image:alt" content="Galerie photos installations recyclage valorisation déchets Groupe Fabre Environnement" />
      </Helmet>

      {showTitle && (
        <header className="text-center mb-8">
          <h2 className="text-2xl font-heading font-bold text-fabre-gray mb-4">
            Galerie Photos - Sites de Valorisation des Déchets
          </h2>
          <div className="w-16 h-1 bg-fabre-green mx-auto mb-6" aria-hidden="true"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Découvrez en images nos installations de recyclage et valorisation des déchets métalliques 
            à travers nos sites en France
          </p>
        </header>
      )}
      
      <div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        role="img"
        aria-label="Galerie d'images des actualités environnementales"
      >
        {images.map((image, index) => {
          const imageTitle = generateImageTitle(image.name, index);
          const imageAlt = generateImageAlt(image.name, index);
          const imageSchema = generateImageSchema(image, index);
          
          return (
            <figure 
              key={image.name}
              className="group overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
              itemScope 
              itemType="https://schema.org/ImageObject"
              onClick={() => openLightbox(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox(index);
                }
              }}
              aria-label={`Agrandir l'image: ${imageTitle}`}
            >
              {/* Données structurées JSON-LD pour chaque image */}
              <script 
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(imageSchema) }}
              />
              
              <div className="relative overflow-hidden">
                <LazyImage
                  src={image.url}
                  alt={imageAlt}
                  title={imageTitle}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  width={400}
                  height={256}
                />
                
                {/* Overlay au hover pour indiquer que c'est cliquable */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white bg-opacity-90 rounded-full p-2">
                    <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Métadonnées Schema.org intégrées */}
              <meta itemProp="name" content={imageTitle} />
              <meta itemProp="alternateName" content={imageAlt} />
              <meta itemProp="description" content={`Photographie installation recyclage valorisation déchets métaux - ${imageTitle}`} />
              <meta itemProp="url" content={image.url} />
              <meta itemProp="contentUrl" content={image.url} />
              <meta itemProp="thumbnailUrl" content={image.url} />
              <meta itemProp="width" content="400" />
              <meta itemProp="height" content="256" />
              <meta itemProp="encodingFormat" content="image/jpeg" />
              <meta itemProp="author" content="Groupe Fabre Environnement" />
              <meta itemProp="copyrightHolder" content="Groupe Fabre Environnement" />
              <meta itemProp="creditText" content="© Groupe Fabre Environnement" />
              <meta itemProp="license" content="https://www.groupefabre-environnement.fr/mentions-legales" />
              <meta itemProp="keywords" content="recyclage, valorisation déchets, métaux, environnement, installation industrielle, Groupe Fabre, France, équipements" />
              
              {/* Caption visible pour le contexte SEO */}
              <figcaption className="sr-only">
                {imageAlt} - Site de recyclage et valorisation des déchets métalliques, équipements industriels de traitement, Groupe Fabre Environnement
              </figcaption>
            </figure>
          );
        })}
      </div>
      
      {images.length > 0 && (
        <footer className="text-center mt-8">
          <p className="text-sm text-gray-500">
            {images.length} image{images.length > 1 ? 's' : ''} disponible{images.length > 1 ? 's' : ''} - Cliquez pour agrandir
          </p>
          <meta itemProp="numberOfItems" content={images.length.toString()} />
        </footer>
      )}

      {/* Lightbox Modal */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] p-0 bg-black/95 border-none">
          <DialogHeader className="absolute top-4 left-4 z-50">
            <DialogTitle className="text-white text-lg">
              {currentImage ? generateImageTitle(currentImage.name, selectedImageIndex!) : ''}
            </DialogTitle>
          </DialogHeader>
          
          {/* Bouton fermer */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
            onClick={closeLightbox}
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Navigation précédent */}
          {selectedImageIndex !== null && selectedImageIndex > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
              onClick={goToPrevious}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
          )}

          {/* Navigation suivant */}
          {selectedImageIndex !== null && selectedImageIndex < images.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
              onClick={goToNext}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          )}

          {/* Image en grand */}
          <div className="flex items-center justify-center h-full min-h-[400px] p-8">
            {currentImage && (
              <img
                src={currentImage.url}
                alt={generateImageAlt(currentImage.name, selectedImageIndex!)}
                className="max-w-full max-h-full object-contain rounded-lg"
                style={{ maxHeight: 'calc(90vh - 100px)' }}
              />
            )}
          </div>

          {/* Informations sur l'image */}
          <div className="absolute bottom-4 left-4 right-4 text-center">
            <p className="text-white/80 text-sm">
              {selectedImageIndex !== null && `${selectedImageIndex + 1} / ${images.length}`}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BlogImageGallery;
